import { c as _c } from "react/compiler-runtime";
import { IcoTrash16 } from '@onefootprint/icons';
import { IconButton, LinkButton } from '@onefootprint/ui';
import { useTranslation } from 'react-i18next';
import { ExpressionEditor } from '@/components/expression-editor';
import { ClickToReveal } from '../../components/shared/inline-edit';
import HighlightedExpression from '../../components/suggestion/primitives/highlighted-expression';
import { tokensFromBundle } from '../../store/autocomplete';
import { useBuilder } from '../../store/provider';
import type { RuleCondition } from '../../store/types';
import { formatExpression } from './format-expression';
type ConditionRowProps = {
  rulesNodeId: string;
  groupId: string;
  condition: RuleCondition;
  index: number;
  canRemove: boolean;
};
const ConditionRow = t0 => {
  const $ = _c(50);
  const {
    rulesNodeId,
    groupId,
    condition,
    index,
    canRemove
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.canvas.rules.condition"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  const {
    state,
    actions,
    isReadOnly
  } = useBuilder();
  const canEdit = !isReadOnly;
  let t2;
  if ($[1] !== rulesNodeId || $[2] !== state.bundle) {
    t2 = tokensFromBundle(state.bundle, rulesNodeId);
    $[1] = rulesNodeId;
    $[2] = state.bundle;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const autocomplete = t2;
  let t3;
  if ($[4] !== condition.expression) {
    t3 = condition.expression.trim();
    $[4] = condition.expression;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const hasExpression = t3.length > 0;
  let t4;
  if ($[6] !== t) {
    t4 = t(_temp);
    $[6] = t;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const placeholder = t4;
  let t5;
  if ($[8] !== condition.expression) {
    t5 = formatExpression(condition.expression);
    $[8] = condition.expression;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const formatted = t5;
  let t6;
  if ($[10] !== index || $[11] !== t) {
    t6 = index === 0 ? t(_temp2) : t(_temp3);
    $[10] = index;
    $[11] = t;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t6) {
    t7 = <span className="text-caption-1 text-tertiary">{t6}</span>;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== actions.rules || $[16] !== canEdit || $[17] !== canRemove || $[18] !== condition.id || $[19] !== groupId || $[20] !== rulesNodeId || $[21] !== t) {
    t8 = canEdit && canRemove && <IconButton size="tiny" aria-label={t(_temp4)} icon={IcoTrash16} destructive={true} onClick={() => actions.rules.removeCondition(rulesNodeId, groupId, condition.id)} />;
    $[15] = actions.rules;
    $[16] = canEdit;
    $[17] = canRemove;
    $[18] = condition.id;
    $[19] = groupId;
    $[20] = rulesNodeId;
    $[21] = t;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== t7 || $[24] !== t8) {
    t9 = <div className="flex items-center justify-between gap-2">{t7}{t8}</div>;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== t) {
    t10 = t(_temp5);
    $[26] = t;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== formatted || $[29] !== hasExpression || $[30] !== placeholder) {
    t11 = hasExpression ? <div className="overflow-x-auto whitespace-pre-wrap wrap-break-word rounded bg-secondary p-3 leading-relaxed"><HighlightedExpression value={formatted} /></div> : <span className="text-tertiary">{placeholder}</span>;
    $[28] = formatted;
    $[29] = hasExpression;
    $[30] = placeholder;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== actions.rules || $[33] !== autocomplete || $[34] !== condition.expression || $[35] !== condition.id || $[36] !== formatted || $[37] !== groupId || $[38] !== placeholder || $[39] !== rulesNodeId || $[40] !== t) {
    t12 = () => <div className="flex flex-col gap-2"><ExpressionEditor variant="textarea" className="min-h-[240px]" value={condition.expression} onChange={next => actions.rules.updateCondition(rulesNodeId, groupId, condition.id, next)} autocompleteItems={autocomplete} placeholder={placeholder} />{formatted !== condition.expression && <LinkButton variant="caption-3" className="self-start" onClick={() => actions.rules.updateCondition(rulesNodeId, groupId, condition.id, formatted)}>{t(_temp6)}</LinkButton>}</div>;
    $[32] = actions.rules;
    $[33] = autocomplete;
    $[34] = condition.expression;
    $[35] = condition.id;
    $[36] = formatted;
    $[37] = groupId;
    $[38] = placeholder;
    $[39] = rulesNodeId;
    $[40] = t;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  let t13;
  if ($[42] !== canEdit || $[43] !== t10 || $[44] !== t11 || $[45] !== t12) {
    t13 = <div className="min-w-0"><ClickToReveal canEdit={canEdit} ariaLabel={t10} className="min-w-0 font-mono text-snippet-2" display={t11}>{t12}</ClickToReveal></div>;
    $[42] = canEdit;
    $[43] = t10;
    $[44] = t11;
    $[45] = t12;
    $[46] = t13;
  } else {
    t13 = $[46];
  }
  let t14;
  if ($[47] !== t13 || $[48] !== t9) {
    t14 = <div className="flex min-w-0 flex-col gap-2">{t9}{t13}</div>;
    $[47] = t13;
    $[48] = t9;
    $[49] = t14;
  } else {
    t14 = $[49];
  }
  return t14;
};
export default ConditionRow;
function _temp($) {
  return $.placeholder;
}
function _temp2($_0) {
  return $_0.if;
}
function _temp3($_1) {
  return $_1.or;
}
function _temp4($_2) {
  return $_2.removeAria;
}
function _temp5($_3) {
  return $_3.editAria;
}
function _temp6($_4) {
  return $_4.format;
}